import { useCallback, useEffect, useRef, useState } from 'react';
import { currentLang } from '../lib/i18n.js';

// The feedback wizard is a third-party page. Preferred presentation is an
// iframe, but two things can stop that: a frame-src CSP rule on the host, and a
// wizard origin that refuses to load (the iframe then settles on about:blank).
// Both are detected at runtime and latch `usePopup`, after which every click
// opens a window instead. There is no way to know in advance which will apply,
// so the fallback has to be reactive.
export function FeedbackFab() {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState('');
  const usePopupRef = useRef(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/feedback/config')
      .then((r) => r.json())
      .then((cfg) => {
        if (!cancelled) setConfig(cfg);
      })
      .catch(() => {
        /* feedback stays hidden when the endpoint is unavailable */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const buildUrl = useCallback(() => {
    const baseUrl = config.url.replace(/\/$/, '');
    // sourceID lets the wizard bucket feedback by the page it came from.
    const p = window.location.pathname.replace(/\//g, '-').replace(/^-|-$/g, '') || 'home';
    const payload = { sourceID: `livedeck-${p}` };
    const lang = currentLang();
    if (lang && lang !== 'en') payload.lang = lang;
    if (config.level) payload.level = config.level;
    return `${baseUrl}/?json=${encodeURIComponent(JSON.stringify(payload))}`;
  }, [config]);

  const openPopup = useCallback(() => {
    const w = 520;
    const h = 720;
    const left = window.screen.width / 2 - w / 2;
    const top = window.screen.height / 2 - h / 2;
    window.open(
      buildUrl(),
      'feedbackWizard',
      `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
  }, [buildUrl]);

  const close = useCallback(() => {
    setOpen(false);
    setSrc('');
  }, []);

  const fallBackToPopup = useCallback(() => {
    usePopupRef.current = true;
    close();
    openPopup();
  }, [close, openPopup]);

  useEffect(() => {
    const onViolation = (e) => {
      if (e.violatedDirective === 'frame-src') fallBackToPopup();
    };
    document.addEventListener('securitypolicyviolation', onViolation);
    return () => document.removeEventListener('securitypolicyviolation', onViolation);
  }, [fallBackToPopup]);

  const onIframeLoad = () => {
    try {
      // A readable contentDocument means same-origin, i.e. the wizard did not
      // load. Cross-origin throws here, which is the success path.
      if (iframeRef.current?.contentDocument?.URL === 'about:blank') fallBackToPopup();
    } catch {
      /* cross-origin — loaded fine */
    }
  };

  const enabled = !!(config && config.enabled && config.url);

  const onClick = () => {
    if (!enabled) return;
    if (usePopupRef.current) {
      openPopup();
      return;
    }
    setSrc(buildUrl());
    setOpen(true);
  };

  return (
    <>
      <button
        className={`feedback-fab${enabled ? ' show' : ''}`}
        aria-label="Feedback"
        title="Give Feedback"
        onClick={onClick}
      >
        <svg viewBox="0 0 24 24">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      <div
        className={`feedback-overlay${open ? ' open' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <div className="feedback-modal">
          <button className="feedback-modal-close" onClick={close}>
            &times;
          </button>
          <iframe ref={iframeRef} title="Feedback Wizard" src={src} onLoad={onIframeLoad} />
        </div>
      </div>
    </>
  );
}
